import { createTheme, type MantineColorsTuple, type MantineTheme } from '@mantine/core';
import type { ThemeId } from '@/types';

/**
 * Everything needed to render one of the app's 6 themes: the two Mantine
 * color tuples (`signal` = accent/actionable, `ink` = structural/text),
 * plus the handful of raw hex values used outside Mantine's theming system
 * (the CSS custom properties consumed by `src/index.css` for the page
 * background/grid, text selection, and scrollbar — none of which Mantine
 * themes natively).
 *
 * Tuple convention (kept identical across every theme so existing component
 * code like `c="ink.7"` or `c="signal.6"` renders sensibly everywhere,
 * without per-theme special-casing): each tuple runs from "low contrast
 * against this theme's page background" (index 0) to "high contrast against
 * this theme's page background" (index 9). For light themes that means
 * pale -> deep hex values; for dark themes it means muted/deep -> bright
 * hex values. See `theme.ts`'s original doc comment for why this matters.
 */
export interface ThemeDefinition {
  id: ThemeId;
  label: string;
  isDark: boolean;
  colorScheme: 'light' | 'dark';
  signal: MantineColorsTuple;
  ink: MantineColorsTuple;
  /** Page background (also `other.surface` / Mantine `white` or `black`, see below). */
  pageBg: string;
  /** Background of "raised" surfaces (cards/modals) above the page background. */
  surfaceRaised: string;
  /** Low-contrast hairline border color, as an rgba() string. */
  hairline: string;
  /** Faint grid-line color drawn over the page background, as an rgba() string. */
  gridLine: string;
  selectionBg: string;
  selectionText: string;
  scrollbarTrack: string;
  scrollbarThumb: string;
  scrollbarThumbHover: string;
  /** Soft elevation shadow used on primary buttons, tuned per-theme (accent-tinted for dark themes, neutral dark for light themes) so raised elements read clearly against each palette. */
  buttonShadow: string;
}

const sharedComponents: NonNullable<Parameters<typeof createTheme>[0]>['components'] = {
  Button: {
    defaultProps: { radius: 0 },
    styles: (theme: MantineTheme) => ({
      root: {
        boxShadow: theme.other.buttonShadow as string,
        fontWeight: 700,
        letterSpacing: '0.03em',
      },
    }),
  },
  Badge: { defaultProps: { radius: 0 } },
  Card: { defaultProps: { radius: 0 } },
  Modal: { defaultProps: { radius: 0 } },
  TextInput: { defaultProps: { radius: 0 } },
  NumberInput: { defaultProps: { radius: 0 } },
  PasswordInput: { defaultProps: { radius: 0 } },
};

/** Builds the Mantine `theme` object for a given palette definition. */
export const buildMantineTheme = (def: ThemeDefinition): MantineTheme =>
  createTheme({
    primaryColor: 'signal',
    colors: { signal: def.signal, ink: def.ink },
    primaryShade: def.isDark ? 6 : 5,
    defaultRadius: 0,
    fontFamily: '"IBM Plex Sans", -apple-system, sans-serif',
    fontFamilyMonospace: '"IBM Plex Mono", ui-monospace, monospace',
    headings: {
      fontFamily: '"Big Shoulders Display", "IBM Plex Sans", sans-serif',
      fontWeight: '800',
      textWrap: 'balance',
    },
    // `black`/`white` are the two fixed high-contrast anchors Mantine falls
    // back to; we point them at this theme's darkest/lightest `ink` shades
    // (index 9 = text-strength contrast, index 1 = near-background) rather
    // than literal black/white so they stay correct for dark themes too.
    black: def.ink[9],
    white: def.ink[1],
    other: {
      surface: def.pageBg,
      surfaceRaised: def.surfaceRaised,
      hairline: def.hairline,
      buttonShadow: def.buttonShadow,
    },
    components: sharedComponents,
  }) as unknown as MantineTheme;

/**
 * "Fleet Blueprint" — a cool, technical, schematic-drawing aesthetic: pale
 * blueprint-paper background, deep navy ink, cyan/teal signal accent. The
 * app's original (and default) theme.
 */
const fleetBlueprint: ThemeDefinition = {
  id: 'fleet-blueprint',
  label: 'Fleet Blueprint',
  isDark: false,
  colorScheme: 'light',
  signal: [
    '#e6fbfa',
    '#c3f3f0',
    '#96e8e3',
    '#65dbd4',
    '#3ecec8',
    '#2a9d8f',
    '#228a7d',
    '#19746a',
    '#125b54',
    '#0a4440',
  ],
  ink: [
    '#f7f9fb',
    '#eef2f5',
    '#dbe3ea',
    '#b9c6d3',
    '#8fa2b5',
    '#647d94',
    '#4a6178',
    '#33495e',
    '#1c3a5e',
    '#0f2338',
  ],
  pageBg: '#eef2f5',
  surfaceRaised: '#ffffff',
  hairline: 'rgba(15, 35, 56, 0.14)',
  gridLine: 'rgba(15, 35, 56, 0.06)',
  selectionBg: '#2a9d8f',
  selectionText: '#f7f9fb',
  scrollbarTrack: '#eef2f5',
  scrollbarThumb: '#b9c6d3',
  scrollbarThumbHover: '#8fa2b5',
  buttonShadow: '0 2px 8px rgba(15, 35, 56, 0.28)',
};

/**
 * "Dispatch Terminal" — near-black dark theme with a safety-orange accent,
 * evoking a fleet dispatch console. Revived from an earlier iteration of
 * the app's design.
 */
const dispatchTerminal: ThemeDefinition = {
  id: 'dispatch-terminal',
  label: 'Dispatch Terminal',
  isDark: true,
  colorScheme: 'dark',
  signal: [
    '#2a1400',
    '#3d1d00',
    '#5c2b00',
    '#7a3900',
    '#a34c00',
    '#cc6000',
    '#ff8000',
    '#ff9933',
    '#ffb366',
    '#ffd9b3',
  ],
  ink: [
    '#0b0d0f',
    '#14171a',
    '#1d2226',
    '#2a3136',
    '#3d454b',
    '#5b6570',
    '#7d8891',
    '#a8b1b8',
    '#cfd5da',
    '#f2f4f5',
  ],
  pageBg: '#0b0d0f',
  surfaceRaised: '#1d2226',
  hairline: 'rgba(242, 244, 245, 0.12)',
  gridLine: 'rgba(255, 128, 0, 0.06)',
  selectionBg: '#ff8000',
  selectionText: '#0b0d0f',
  scrollbarTrack: '#0b0d0f',
  scrollbarThumb: '#3d454b',
  scrollbarThumbHover: '#5b6570',
  buttonShadow: '0 2px 10px rgba(255, 128, 0, 0.35)',
};

/** "Daybreak Fjord" — warm sunrise-over-cold-water light theme: cream/slate-blue base, amber-coral accent. */
const daybreakFjord: ThemeDefinition = {
  id: 'daybreak-fjord',
  label: 'Daybreak Fjord',
  isDark: false,
  colorScheme: 'light',
  signal: [
    '#fff3e6',
    '#ffe0bf',
    '#ffc98f',
    '#ffb163',
    '#ff9a3d',
    '#f3791f',
    '#d8631a',
    '#b04f16',
    '#823a10',
    '#57270a',
  ],
  ink: [
    '#f8f9fa',
    '#eef1f3',
    '#dde3e8',
    '#c3cdd6',
    '#9aa8b5',
    '#71828f',
    '#576773',
    '#3f4d59',
    '#2b3740',
    '#1a232b',
  ],
  pageBg: '#eef1f3',
  surfaceRaised: '#fffaf3',
  hairline: 'rgba(26, 35, 43, 0.14)',
  gridLine: 'rgba(26, 35, 43, 0.06)',
  selectionBg: '#f3791f',
  selectionText: '#f8f9fa',
  scrollbarTrack: '#eef1f3',
  scrollbarThumb: '#c3cdd6',
  scrollbarThumbHover: '#9aa8b5',
  buttonShadow: '0 2px 8px rgba(26, 35, 43, 0.28)',
};

/** "Paper Manifest" — warm ledger-paper light theme: cream/graphite base, forest-green accent. */
const paperManifest: ThemeDefinition = {
  id: 'paper-manifest',
  label: 'Paper Manifest',
  isDark: false,
  colorScheme: 'light',
  signal: [
    '#eaf6ec',
    '#c9ecd0',
    '#a0dfad',
    '#74d089',
    '#4cbf6c',
    '#2fa350',
    '#268a43',
    '#1d6f36',
    '#155327',
    '#0d3819',
  ],
  ink: [
    '#faf8f5',
    '#f0ece4',
    '#ddd5c8',
    '#bdb2a0',
    '#948976',
    '#726757',
    '#564d40',
    '#403830',
    '#2b2620',
    '#181511',
  ],
  pageBg: '#f0ece4',
  surfaceRaised: '#fffdf9',
  hairline: 'rgba(24, 21, 17, 0.14)',
  gridLine: 'rgba(24, 21, 17, 0.05)',
  selectionBg: '#2fa350',
  selectionText: '#faf8f5',
  scrollbarTrack: '#f0ece4',
  scrollbarThumb: '#bdb2a0',
  scrollbarThumbHover: '#948976',
  buttonShadow: '0 2px 8px rgba(24, 21, 17, 0.28)',
};

/** "Midnight Convoy" — deep indigo-navy dark theme, violet/periwinkle accent. */
const midnightConvoy: ThemeDefinition = {
  id: 'midnight-convoy',
  label: 'Midnight Convoy',
  isDark: true,
  colorScheme: 'dark',
  signal: [
    '#1c1233',
    '#2a1a4d',
    '#3e2570',
    '#573599',
    '#7248c2',
    '#8f63dd',
    '#a67df0',
    '#bd9bf5',
    '#d3bafa',
    '#ece3fd',
  ],
  ink: [
    '#0b0d16',
    '#12151f',
    '#1a1f2c',
    '#262d3c',
    '#3a4356',
    '#565f74',
    '#7a8296',
    '#a3aabc',
    '#c8cdda',
    '#eceffa',
  ],
  pageBg: '#12151f',
  surfaceRaised: '#1a1f2c',
  hairline: 'rgba(236, 239, 250, 0.12)',
  gridLine: 'rgba(142, 99, 221, 0.08)',
  selectionBg: '#8f63dd',
  selectionText: '#0b0d16',
  scrollbarTrack: '#12151f',
  scrollbarThumb: '#3a4356',
  scrollbarThumbHover: '#565f74',
  buttonShadow: '0 2px 10px rgba(142, 99, 221, 0.35)',
};

/** "Graphite Run" — industrial charcoal dark theme, high-vis acid-lime accent. */
const graphiteRun: ThemeDefinition = {
  id: 'graphite-run',
  label: 'Graphite Run',
  isDark: true,
  colorScheme: 'dark',
  signal: [
    '#1a2200',
    '#263200',
    '#344500',
    '#455c00',
    '#597300',
    '#729400',
    '#8fb800',
    '#aad83d',
    '#c6ef7a',
    '#e3f9bd',
  ],
  ink: [
    '#0d0d0d',
    '#161616',
    '#202020',
    '#2d2d2d',
    '#404040',
    '#5c5c5c',
    '#7d7d7d',
    '#a3a3a3',
    '#c9c9c9',
    '#efefef',
  ],
  pageBg: '#161616',
  surfaceRaised: '#202020',
  hairline: 'rgba(239, 239, 239, 0.12)',
  gridLine: 'rgba(143, 184, 0, 0.08)',
  selectionBg: '#8fb800',
  selectionText: '#0d0d0d',
  scrollbarTrack: '#161616',
  scrollbarThumb: '#404040',
  scrollbarThumbHover: '#5c5c5c',
  buttonShadow: '0 2px 10px rgba(143, 184, 0, 0.35)',
};

export const THEME_DEFINITIONS: Record<ThemeId, ThemeDefinition> = {
  'fleet-blueprint': fleetBlueprint,
  'daybreak-fjord': daybreakFjord,
  'paper-manifest': paperManifest,
  'dispatch-terminal': dispatchTerminal,
  'midnight-convoy': midnightConvoy,
  'graphite-run': graphiteRun,
};

/** Ordered for UI display: light themes first, then dark themes. */
export const THEME_LIST: ThemeDefinition[] = [
  fleetBlueprint,
  daybreakFjord,
  paperManifest,
  dispatchTerminal,
  midnightConvoy,
  graphiteRun,
];

export const DEFAULT_THEME_ID: ThemeId = 'fleet-blueprint';
